import { ChangeDetectionStrategy, Component, forwardRef, signal, viewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TuiIcon, TuiTextfield, TuiTextfieldComponent } from '@taiga-ui/core';
import { TuiPassword } from '@taiga-ui/kit';
import { noop } from 'rxjs';

import { PasswordRulesPanelComponent } from './password-rules-panel/password-rules-panel.component';
import { PasswordsMatchValidatorDirective } from './password-validators/password-match-validator.directive';
import { PasswordValidatorDirective } from './password-validators/password-validator.directive';

export type NewPasswordValue = {
  password: string;
  passwordConfirm: string;
};

@Component({
  selector: 'ion-new-password-input',
  templateUrl: './new-password-input.component.html',
  imports: [
    FormsModule,
    TuiTextfieldComponent,
    TuiIcon,
    TuiTextfield,
    TuiPassword,
    TranslateModule,
    PasswordRulesPanelComponent,
    PasswordValidatorDirective,
    PasswordsMatchValidatorDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NewPasswordInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NewPasswordInputComponent),
      multi: true,
    },
  ],
})
export class NewPasswordInputComponent implements ControlValueAccessor, Validator {
  readonly internalForm = viewChild<NgForm>('resetForm');

  readonly areRulesVisible = signal(false);
  readonly isTouched = signal(false);
  readonly isDisabled = signal(false);

  model: NewPasswordValue = {
    password: '',
    passwordConfirm: '',
  };

  private onChange: (value: NewPasswordValue) => void = noop;
  private onTouched: () => void = noop;
  private onValidationChange: () => void = noop;

  onCredentialsChange(): void {
    this.areRulesVisible.set(true);
    this.onChange(this.model);
    this.onValidationChange();
  }

  onBlur(): void {
    this.isTouched.set(true);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: NewPasswordValue | null): void {
    if (value) {
      this.model = { ...value };
    } else {
      this.model = {
        password: '',
        passwordConfirm: '',
      };
    }
  }

  registerOnChange(fn: (value: NewPasswordValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // Validator implementation
  validate(): ValidationErrors | null {
    const form = this.internalForm();

    // Return null if form is not yet initialized or valid
    if (!form || !form.invalid) {
      return null;
    }

    // Return the internal form's errors to the parent form
    return { passwordInvalid: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }
}
